'use client'

import { RefObject, useCallback, useEffect, useRef } from 'react'

import {
  clickEvent,
  firstTimeListerEducationPageLeaveEvent,
  firstTimeListerEducationExampleSwitchEvent,
  markFirstTimeListerEducationAsRead,
} from '@marketplace-web/conversation-safety/conversation-safety-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

const LAST_SLIDE_INDEX = 2

type Props = {
  educationContainerRef: RefObject<HTMLDivElement | null>
  isNewVariant?: boolean
  currentConversationIndex?: number
  maxConversationIndex?: number
  getExampleDuration?: (slideIndex: number, exampleIndex: number) => number
  currentSlideIndex?: number
}

const getScrollDepth = (element: HTMLDivElement, scrollOffset = 0) => {
  const documentHeight = element.scrollHeight
  const clientHeight = element.clientHeight + scrollOffset

  if (clientHeight <= 0) {
    return 0
  }

  return Math.floor((clientHeight / documentHeight) * 100)
}

const getExampleScrollDepth = (currentIndex: number, maxIndex: number) => {
  if (maxIndex === 0) return 100

  return Math.floor(((currentIndex + 1) / (maxIndex + 1)) * 100)
}

const usePhishingFirstTimeListerEducationTracking = ({
  educationContainerRef,
  isNewVariant = false,
  currentConversationIndex = 0,
  maxConversationIndex = 0,
  getExampleDuration,
  currentSlideIndex = 0,
}: Props) => {
  const initialTime = useRef(new Date().getTime())
  const initialPageScrollDepth = useRef(0)
  const maxPageScrollDepth = useRef(0)
  const exampleStartTime = useRef(new Date().getTime())
  const previousExampleIndex = useRef(currentConversationIndex)
  const isSlideChanging = useRef(false)

  const { track } = useTracking()

  const handleScrollDepthUpdate = useCallback(() => {
    const element = educationContainerRef.current

    if (!element) return

    if (isNewVariant) {
      const initialScrollDepth = getExampleScrollDepth(0, maxConversationIndex)
      const currentScrollDepth = getExampleScrollDepth(
        currentConversationIndex,
        maxConversationIndex,
      )

      initialPageScrollDepth.current = initialScrollDepth

      if (currentScrollDepth > maxPageScrollDepth.current) {
        maxPageScrollDepth.current = currentScrollDepth
      }
    } else {
      const initialScrollDepth = getScrollDepth(element)
      const currentScrollDepth = getScrollDepth(element, element.scrollTop)

      initialPageScrollDepth.current = initialScrollDepth

      if (currentScrollDepth > maxPageScrollDepth.current) {
        maxPageScrollDepth.current = currentScrollDepth
      }
    }
  }, [educationContainerRef, isNewVariant, currentConversationIndex, maxConversationIndex])

  const resetTrackingData = () => {
    initialTime.current = new Date().getTime()
    maxPageScrollDepth.current = 0
  }

  const resetExampleTrackingData = useCallback(() => {
    exampleStartTime.current = new Date().getTime()
    previousExampleIndex.current = currentConversationIndex
  }, [currentConversationIndex])

  const trackFirstTimeListerEducationExampleSwitchEvent = useCallback(
    (pageNumber: number, fromExampleIndex: number, toExampleIndex: number) => {
      const currentTime = new Date().getTime()
      const timeSpentOnExample = currentTime - exampleStartTime.current
      const automaticTransitionTime = getExampleDuration
        ? getExampleDuration(pageNumber, fromExampleIndex)
        : 0

      track(
        firstTimeListerEducationExampleSwitchEvent({
          pageNumber: pageNumber + 1,
          exampleNumber: fromExampleIndex + 1,
          goingToExampleNumber: toExampleIndex + 1,
          timeSpentOnExampleMs: timeSpentOnExample,
          automaticTransitionTimeMs: automaticTransitionTime,
        }),
      )

      resetExampleTrackingData()
    },
    [track, getExampleDuration, resetExampleTrackingData],
  )

  const trackFirstTimeListerEducationPageLeaveEvent = (
    slideIndex: number,
    nextSlideIndex: number | null,
  ) => {
    handleScrollDepthUpdate()

    const currentTime = new Date().getTime()

    track(
      firstTimeListerEducationPageLeaveEvent({
        pageNumber: slideIndex + 1,
        goingToPageNumber: typeof nextSlideIndex === 'number' ? nextSlideIndex + 1 : null,
        timeSpentOnPageMs: currentTime - initialTime.current,
        initialPageScrollDepth: initialPageScrollDepth.current,
        pageScrollDepth: maxPageScrollDepth.current,
      }),
    )
  }

  const onSeeTipsClick = () => {
    resetTrackingData()
    resetExampleTrackingData()

    track(
      clickEvent({
        target: 'first_time_lister_education_see_tips',
        screen: 'first_time_lister_education',
      }),
    )
  }

  const onSlideChange = (slideIndex: number, nextSlideIndex: number) => {
    isSlideChanging.current = true

    trackFirstTimeListerEducationPageLeaveEvent(slideIndex, nextSlideIndex)

    resetTrackingData()
    resetExampleTrackingData()

    setTimeout(() => {
      isSlideChanging.current = false
    }, 0)
  }

  const onHelpCenterClick = () => {
    track(
      clickEvent({
        target: 'first_time_lister_education_show_help_center',
        screen: 'first_time_lister_education',
      }),
    )
  }

  const onDialogClose = () => {
    markFirstTimeListerEducationAsRead()

    track(
      clickEvent({
        target: 'first_time_lister_education_close',
        screen: 'first_time_lister_education',
      }),
    )
    trackFirstTimeListerEducationPageLeaveEvent(LAST_SLIDE_INDEX, null)
  }

  useEffect(() => {
    const element = educationContainerRef.current

    if (!element) return undefined

    element.addEventListener('scroll', handleScrollDepthUpdate)

    return () => {
      element.removeEventListener('scroll', handleScrollDepthUpdate)
    }
  }, [educationContainerRef, handleScrollDepthUpdate])

  useEffect(() => {
    if (
      isNewVariant &&
      !isSlideChanging.current &&
      previousExampleIndex.current !== currentConversationIndex
    ) {
      trackFirstTimeListerEducationExampleSwitchEvent(
        currentSlideIndex,
        previousExampleIndex.current,
        currentConversationIndex,
      )
    }
  }, [
    currentConversationIndex,
    currentSlideIndex,
    trackFirstTimeListerEducationExampleSwitchEvent,
    isNewVariant,
  ])

  return { onSeeTipsClick, onSlideChange, onHelpCenterClick, onDialogClose }
}

export default usePhishingFirstTimeListerEducationTracking
